import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTooltip } from "context/TooltipProvider";
import {
  addReport,
  fetchAppointmentReasons,
  fetchAssetReferralById,
} from "services/assetRecoveryService";
import { MultipleFileUploadComponent } from "components/form/MultipleFileUpload";
import { Asset, ProhibitionRequest, Referral } from "types";

interface DailyReportProps {
  referralId?: string;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
  onNewDataAdded?: (data: any) => void;
  referral: Referral | null;
  assets: Asset[] | null;
  prohibitionRecord: ProhibitionRequest[] | null; // Renamed for clarity
}

export const DailyReport: React.FC<DailyReportProps> = ({
  referralId,
  setActiveTab,
  onNewDataAdded,
  referral,
  assets,
  prohibitionRecord,
}) => {
  const [formData, setFormData] = useState<{
    prohibition_id: string; // Added prohibition_id
    court_hearing_date: string;
    daily_report_summary: string;
    daily_report_summary_file: string;
    court_order_for_next_appointment: string;
    next_appointment_reasons: string;
    next_appointment_date: string;
    assets: string[];
  }>({
    prohibition_id: "", // Initialize empty
    court_hearing_date: "",
    daily_report_summary: "",
    daily_report_summary_file: "",
    court_order_for_next_appointment: "",
    next_appointment_reasons: "",
    next_appointment_date: "",
    assets: [],
  });

  const [submitting, setSubmitting] = useState(false);
  const [reasons, setReasons] = useState<{ id: string; name: string }[]>([]);
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();

  const handleInputChange = (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
      | { target: { name: string; value: string | string[] | boolean } },
  ) => {
    const { name, value } = e.target;

    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  // // Filter prohibition records that are in litigation status (where reports can be added)
  // const prohibitionRecord? =
  //   eligibleprohibitionRecord?s?.filter(
  //     (record) =>
  //       record.status === "Prohibition On litigation" ||
  //       record.status === "PROHIBITION_ON_LITIGATION",
  //   ) || [];

  const handleAddDailyReport = async () => {
    // Validate required fields
    if (!formData.prohibition_id) {
      showTooltip(t("please_select_prohibition_record"), "error");
      return;
    }

    if (!formData.daily_report_summary.trim()) {
      showTooltip(
        t("daily_report_summary_required", {
          defaultValue: "Daily report summary is required",
        }),
        "error",
      );
      return;
    }

    if (!formData.court_hearing_date) {
      showTooltip(
        t("court_hearing_date_required", {
          defaultValue: "Court hearing date is required",
        }),
        "error",
      );
      return;
    }

    const data = new FormData();
    data.append("prohabition_request", formData.prohibition_id); // Changed from referral to prohibition
    data.append("court_hearing_date", formData.court_hearing_date);
    data.append("daily_report_summary", formData.daily_report_summary);
    data.append("referral", referralId as string);
    data.append(
      "court_order_for_next_appointment",
      formData.court_order_for_next_appointment || "",
    );
    data.append(
      "next_appointment_reasons",
      formData.next_appointment_reasons || "",
    );
    data.append("next_appointment_date", formData.next_appointment_date || "");

    if (uploadedFiles.length > 0) {
      uploadedFiles.forEach((file) => {
        data.append("daily_report_summary_documents", file);
      });
    }

    try {
      setSubmitting(true);

      // Always use addReport regardless of status, but with prohibition ID
      const response = await addReport(data);

      if (onNewDataAdded && response) {
        showTooltip(
          t("daily_report_submitted_successfully", {
            defaultValue: "Daily report submitted successfully",
          }),
          "success",
        );

        setFormData({
          prohibition_id: "",
          court_hearing_date: "",
          daily_report_summary: "",
          daily_report_summary_file: "",
          court_order_for_next_appointment: "",
          next_appointment_reasons: "",
          next_appointment_date: "",
          assets: [],
        });
        setUploadedFiles([]);

        // Refresh the referral data to get updated prohibition records with reports
        const updatedReferral = await fetchAssetReferralById(
          referralId as string,
        );
        if (updatedReferral) {
          onNewDataAdded(updatedReferral);
          setActiveTab?.("Prohibition Reports");
        }
      }
    } catch (err) {
      console.error("Error submitting daily report:", err);
      showTooltip(
        t("daily_report_not_submitted_successfully", {
          defaultValue: "Daily report not submitted successfully",
        }),
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    fetchAppointmentReasons().then(setReasons).catch(console.error);
  }, []);

  return (
    <div className="p-6">
      <p className="text-sm mb-2 text-gray-400">Fill Daily Report</p>

      {/* Show message if no eligible prohibition records */}
      {prohibitionRecord?.length === 0 && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm text-yellow-700">
            No prohibition records in litigation status. Reports can only be
            added to prohibitions that are in litigation.
          </p>
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleAddDailyReport();
        }}
      >
        <div className="flex flex-col gap-2">
          {/* Prohibition Selection Dropdown */}
          <div className="md:col-span-2 flex flex-col">
            <label className="referral-popup-label">
              Select Prohibition Record <span className="text-red-500">*</span>
            </label>
            <select
              className="referral-form-field"
              onChange={handleInputChange}
              name="prohibition_id"
              value={formData.prohibition_id || ""}
              required
              disabled={prohibitionRecord?.length === 0}
            >
              <option value="" disabled>
                {prohibitionRecord?.length === 0
                  ? "No prohibitions in litigation"
                  : "Select a prohibition record"}
              </option>
              {prohibitionRecord?.map((record) => (
                <option value={record.id} key={record.id}>
                  {record.prohabition_request_registration_number ||
                    `Prohibition #${record.id.slice(-6)}`}{" "}
                  -
                  {record.prohabition_request_summary?.substring(0, 50) ||
                    "No summary"}
                  {record.prohabition_court_name
                    ? ` (${record.prohabition_court_name})`
                    : ""}
                </option>
              ))}
            </select>
          </div>

          {/* Daily Report Summary */}
          <div className="md:col-span-2 flex flex-col">
            <label className="referral-popup-label">Report Summary</label>
            <textarea
              className="referral-form-field min-h-[100px] w-full"
              onChange={handleInputChange}
              name="daily_report_summary"
              placeholder="daily report detail here..."
              value={formData.daily_report_summary || ""}
            />
          </div>

          {/* Report Document Upload */}
          <div className="md:col-span-2">
            <MultipleFileUploadComponent
              onFilesChange={setUploadedFiles}
              uploadedFiles={uploadedFiles}
              label="Report Doc"
            />
          </div>

          {/* Appointment Date */}
          <div className="flex flex-col">
            <label className="referral-popup-label">Date</label>
            <input
              type="date"
              className="referral-form-field"
              onChange={handleInputChange}
              name="court_hearing_date"
              value={formData.court_hearing_date || ""}
            />
          </div>

          <div className="flex flex-col">
            <label className="referral-popup-label">
              Next Appointment Date
            </label>
            <input
              type="date"
              className="referral-form-field"
              onChange={handleInputChange}
              name="next_appointment_date"
              value={formData.next_appointment_date || ""}
            />
          </div>

          {/* Appointment Reason */}
          <div className="md:col-span-2">
            <label className="referral-popup-label">Appointment Reason</label>
            <select
              className="referral-form-field"
              onChange={handleInputChange}
              name="next_appointment_reasons"
              value={formData.next_appointment_reasons || ""}
            >
              <option value="" disabled>
                Select Appointment Reason
              </option>
              {reasons?.map((reason) => (
                <option value={reason?.name} key={reason?.id}>
                  {reason.name}
                </option>
              ))}
            </select>
          </div>

          {/* Court Order for Next Appointment */}
          <div className="md:col-span-2 flex flex-col">
            <label className="referral-popup-label">
              Court Order for Next Appointment
            </label>
            <textarea
              className="referral-form-field min-h-[80px]"
              onChange={handleInputChange}
              name="court_order_for_next_appointment"
              placeholder="Enter court order details..."
              value={formData.court_order_for_next_appointment || ""}
            />
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <button
            type="submit"
            className="referral-form-button text-white px-8 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={submitting || prohibitionRecord?.length === 0}
          >
            {submitting ? "Submitting..." : "Set Appointment"}
          </button>
        </div>
      </form>
    </div>
  );
};
